import { http_v2,http } from "@api/client";
import { paramterize, pickResponse } from "@api/utils";
import { DuplicateDecisionPayload } from "@pages/content-duplicate-spotter/types";

const DUPLICATE_PAGE_SIZE = 5000;
const PAGE_CONCURRENCY = 3;
const PAGE_RETRIES = 3;

export interface DuplicateSpotterProgress {
  processed: number;
  total?: number;
  remaining?: number;
  page: number;
  status: string;
}

export interface DuplicateSpotterDateRange {
  startDate: string;
  endDate: string;
}

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const getRecordsFromResponse = (response: any): any[] => {
  const records = response?.data?.data ?? response?.data ?? response;
  return Array.isArray(records) ? records : [];
};

const getTotalFromResponse = (response: any): number | undefined => {
  const total =
    response?.data?.total ??
    response?.data?.count ??
    response?.total;

  return typeof total === 'number' ? total : undefined;
};

const fetchDuplicateSpotterPage = async (
  page: number,
  paramsFactory: (page: number) => URLSearchParams
): Promise<{ page: number; records: any[]; total?: number; failed: boolean }> => {
  let lastError: unknown;

  for (let attempt = 0; attempt <= PAGE_RETRIES; attempt += 1) {
    try {
      const response = await http
        .get('/content-duplicate-spotter', { params: paramsFactory(page) })
        .pipe(pickResponse())
        .toPromise();

      return {
        page,
        records: getRecordsFromResponse(response),
        total: getTotalFromResponse(response),
        failed: false,
      };
    } catch (error) {
      lastError = error;

      if (attempt < PAGE_RETRIES) {
        await delay(400 * (attempt + 1));
      }
    }
  }

  console.error(
    `[Duplicate Spotter] Page ${page} failed after ${PAGE_RETRIES + 1} attempts`,
    lastError
  );

  return {
    page,
    records: [],
    failed: true,
  };
};

const formatLocalYmd = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const defaultDateRange = (): DuplicateSpotterDateRange => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - 30);

  return {
    startDate: formatLocalYmd(startDate),
    endDate: formatLocalYmd(endDate),
  };
};

const buildCreatedAtFilter = (dateRange?: DuplicateSpotterDateRange) => {
  const range =
    dateRange?.startDate && dateRange?.endDate ? dateRange : defaultDateRange();

  return `$gte|||${range.startDate};$lte|||${range.endDate}`;
};

export const getContentForDuplicateSpotter = async (
  onProgress?: (progress: DuplicateSpotterProgress) => void,
  dateRange?: DuplicateSpotterDateRange
) => {
  const recordsByPage = new Map<number, any[]>();
  const createdAtFilter = buildCreatedAtFilter(dateRange);

  const paramsFactory = (page: number) =>
    paramterize({
      page_size: DUPLICATE_PAGE_SIZE,
      page,
      created_at: createdAtFilter,
    });

  const reportProgress = (
    page: number,
    total?: number,
    status?: string
  ) => {
    const processed = Array.from(recordsByPage.values()).reduce(
      (count, records) => count + records.length,
      0
    );

    onProgress?.({
      processed,
      total,
      remaining:
        typeof total === 'number' ? Math.max(total - processed, 0) : undefined,
      page,
      status:
        status ||
        `Processed ${processed.toLocaleString()} records. Fetching more...`,
    });
  };

  const firstPage = await fetchDuplicateSpotterPage(1, paramsFactory);

  if (!firstPage.failed) {
    recordsByPage.set(1, firstPage.records);
  }

  let catalogTotal = firstPage.total;
  let lastPage = 1;

  reportProgress(1, catalogTotal);

  let nextPage = 2;
  let reachedEnd =
    !firstPage.failed && firstPage.records.length < DUPLICATE_PAGE_SIZE;
  let consecutiveFailedBatches = 0;

  while (!reachedEnd) {
    const knownPageCount =
      typeof catalogTotal === 'number' && catalogTotal > 0
        ? Math.ceil(catalogTotal / DUPLICATE_PAGE_SIZE)
        : undefined;

    const batchPages: number[] = [];

    while (
      batchPages.length < PAGE_CONCURRENCY &&
      (knownPageCount === undefined || nextPage <= knownPageCount)
    ) {
      batchPages.push(nextPage);
      nextPage += 1;
    }

    if (batchPages.length === 0) {
      break;
    }

    const batchResults = await Promise.all(
      batchPages.map(page => fetchDuplicateSpotterPage(page, paramsFactory))
    );

    let successfulShortPage = false;
    let batchHadSuccess = false;

    batchResults
      .sort((left, right) => left.page - right.page)
      .forEach(result => {
        lastPage = Math.max(lastPage, result.page);

        if (typeof result.total === 'number') {
          catalogTotal = result.total;
        }

        if (result.failed) {
          return;
        }

        batchHadSuccess = true;
        recordsByPage.set(result.page, result.records);

        if (result.records.length < DUPLICATE_PAGE_SIZE) {
          successfulShortPage = true;
        }
      });

    const processed = Array.from(recordsByPage.values()).reduce(
      (count, records) => count + records.length,
      0
    );

    reportProgress(lastPage, catalogTotal);

    if (successfulShortPage) {
      reachedEnd = true;
      break;
    }

    if (
      typeof catalogTotal === 'number' &&
      (processed >= catalogTotal || lastPage >= Math.ceil(catalogTotal / DUPLICATE_PAGE_SIZE))
    ) {
      reachedEnd = true;
      break;
    }

    if (batchHadSuccess) {
      consecutiveFailedBatches = 0;
    } else {
      consecutiveFailedBatches += 1;

      /*
       * Keep walking pages after a failed batch.
       * Stop only when we do not know the total and several
       * batches in a row fail, so we do not loop forever.
       */
      if (knownPageCount === undefined && consecutiveFailedBatches >= 3) {
        console.error(
          '[Duplicate Spotter] Stopping after 3 consecutive failed page batches'
        );
        break;
      }
    }
  }

  const allRecords = Array.from(recordsByPage.entries())
    .sort(([left], [right]) => left - right)
    .flatMap(([, records]) => records);

  onProgress?.({
    processed: allRecords.length,
    total: catalogTotal ?? allRecords.length,
    remaining: 0,
    page: lastPage,
    status: `Loaded ${allRecords.length.toLocaleString()} records. Starting duplicate analysis...`,
  });

  return allRecords;
};

export const getNotDuplicateDecisions = async () => {
  return http.get('/content-duplicate-decisions').pipe(pickResponse())
      .toPromise();
}

export const markAsNotDuplicate = async (payload:  DuplicateDecisionPayload) => {
  return http.post(`/content-duplicate-decisions`, payload).pipe(pickResponse())
      .toPromise();
}